import { Alert, AlertTitle, Button, Card, CardContent, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useQueryClient } from "@tanstack/react-query";
import React, { Suspense, lazy, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { financeApi } from "../../lib/api";
import { resolveAuthError, type AuthField, type AuthUserError } from "./authErrors";
import type { AuthFailureOperation } from "./authErrorLogging";

// Decorative canvas animation — code-split so it never blocks the login form.
const CurrencySmokePanel = lazy(() => import("../debug/CurrencySmokePanel").then((module) => ({ default: module.CurrencySmokePanel })));

export function AuthPage({ mode: initialMode }: { mode: "login" | "register" }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<AuthField, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registerStep, setRegisterStep] = useState<"details" | "verify">("details");
  const [verifyEmail, setVerifyEmail] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const isRegister = initialMode === "register";

  // Reset the multi-step register flow whenever the route (login/register) changes,
  // so navigating between Login and Create one always lands on a clean form.
  useEffect(() => {
    setRegisterStep("details");
    setVerifyEmail("");
    setVerifyCode("");
    setCooldownSeconds(0);
    setStatus("");
    setFieldErrors({});
  }, [initialMode]);

  // Tick the resend cooldown down to zero once it has been set (after register or resend).
  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const id = setTimeout(() => setCooldownSeconds((seconds) => seconds - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldownSeconds]);

  const showAuthFailure = (error: unknown, operation: AuthFailureOperation) => {
    const userError: AuthUserError = resolveAuthError(error, operation);
    setFieldErrors(userError.fieldErrors);
    setStatus(userError.formError || "");
  };

  const clearFieldError = (field: AuthField) => {
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
    setStatus("");
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus("");
    setFieldErrors({});
    setIsSubmitting(true);
    const data = new FormData(event.currentTarget);
    const email = String(data.get("email") || "");
    const password = String(data.get("password") || "");
    const displayName = String(data.get("display_name") || "");

    try {
      if (isRegister && registerStep === "details") {
        // Step 1: Create account
        await financeApi.register({
          email,
          password,
          display_name: displayName,
        });
        setVerifyEmail(email);
        setRegisterStep("verify");
        // Fetch initial cooldown
        financeApi.getVerificationCooldown(email).then((res) => setCooldownSeconds(res.seconds_remaining)).catch(() => {});
      } else if (!isRegister) {
        // Login
        const result = await financeApi.login({ email, password });
        localStorage.setItem("financeToken", result.access_token);
        await queryClient.invalidateQueries();
        navigate("/home", { replace: true });
      }
    } catch (error) {
      showAuthFailure(error, isRegister ? "register" : "login");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendCode = async () => {
    if (!verifyEmail || cooldownSeconds > 0) return;
    setStatus("");
    setFieldErrors({});
    try {
      await financeApi.sendVerificationCode(verifyEmail);
      const res = await financeApi.getVerificationCooldown(verifyEmail);
      setCooldownSeconds(res.seconds_remaining);
    } catch (error) {
      showAuthFailure(error, "resend");
    }
  };

  const handleVerifyCode = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus("");
    setFieldErrors({});
    if (verifyCode.length !== 6) return;
    try {
      const result = await financeApi.verifyCode(verifyEmail, verifyCode);
      if (result.verified) {
        localStorage.setItem("financeToken", result.access_token);
        await queryClient.invalidateQueries();
        navigate("/groups/new", { replace: true });
      } else {
        showAuthFailure(result.message, "verify");
      }
    } catch (error) {
      showAuthFailure(error, "verify");
    }
  };

  return (
    <section className="auth-shell">
      <Card className="auth-card">
        <Suspense fallback={null}>
          <CurrencySmokePanel />
        </Suspense>
        <CardContent>
          <h2>{isRegister ? "Register" : "Login"}</h2>
          {isRegister && registerStep === "details" ? (
            <form className="react-auth-form" onSubmit={submit}>
              <TextField
                className="react-auth-input"
                label="Email"
                name="email"
                type="email"
                autoComplete="email"
                error={Boolean(fieldErrors.email)}
                helperText={fieldErrors.email}
                onChange={() => clearFieldError("email")}
                required
              />
              <TextField
                className="react-auth-input"
                label="Password"
                name="password"
                type={showRegisterPassword ? "text" : "password"}
                slotProps={{
                  htmlInput: { minLength: 8 },
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          type="button"
                          aria-label={showRegisterPassword ? "Hide new password" : "Show new password"}
                          title={showRegisterPassword ? "Hide password" : "Show password"}
                          onClick={() => setShowRegisterPassword((current) => !current)}
                        >
                          {showRegisterPassword ? <VisibilityOffOutlinedIcon aria-hidden="true" /> : <VisibilityOutlinedIcon aria-hidden="true" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                autoComplete="new-password"
                error={Boolean(fieldErrors.password)}
                helperText={fieldErrors.password}
                onChange={() => clearFieldError("password")}
                required
              />
              <TextField
                className="react-auth-input"
                label="Name"
                name="display_name"
                error={Boolean(fieldErrors.display_name)}
                helperText={fieldErrors.display_name}
                onChange={() => clearFieldError("display_name")}
                required
              />
              {status && <Alert severity="error">{status}</Alert>}
              <Button className="primary-link" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Creating account..." : "Create account"}
              </Button>
            </form>
          ) : isRegister && registerStep === "verify" ? (
            <div className="react-auth-form">
              <p>Enter the 6-digit code sent to <strong>{verifyEmail}</strong></p>
              <form className="react-auth-form" style={{ marginTop: 0 }} onSubmit={handleVerifyCode}>
                <TextField
                  className="react-auth-input"
                  label="Verification code"
                  name="verification_code"
                  type="text"
                  inputMode="numeric"
                  value={verifyCode}
                  onChange={(e) => {
                    setVerifyCode(e.target.value.slice(0, 6));
                    clearFieldError("verification_code");
                  }}
                  autoComplete="one-time-code"
                  error={Boolean(fieldErrors.verification_code)}
                  helperText={fieldErrors.verification_code}
                  required
                  autoFocus
                />
                {status && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <AlertTitle>Error</AlertTitle>
                    {status}
                  </Alert>
                )}
                <Button className="primary-link" type="submit" disabled={verifyCode.length !== 6}>
                  Verify code
                </Button>
              </form>
              <div className="resend-code-section" style={{ marginTop: 16 }}>
                {cooldownSeconds > 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Resend code in {cooldownSeconds}s
                  </Typography>
                ) : (
                  <Button size="small" onClick={handleSendCode} disabled={isSubmitting}>
                    Resend code
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <form className="react-auth-form" onSubmit={submit}>
              <TextField
                className="react-auth-input"
                label="Email"
                name="email"
                type="email"
                autoComplete="email"
                error={Boolean(fieldErrors.email)}
                helperText={fieldErrors.email}
                onChange={() => clearFieldError("email")}
                required
              />
              <TextField
                className="react-auth-input"
                label="Password"
                name="password"
                type={showLoginPassword ? "text" : "password"}
                autoComplete="current-password"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          type="button"
                          aria-label={showLoginPassword ? "Hide password" : "Show password"}
                          title={showLoginPassword ? "Hide password" : "Show password"}
                          onClick={() => setShowLoginPassword((current) => !current)}
                        >
                          {showLoginPassword ? <VisibilityOffOutlinedIcon aria-hidden="true" /> : <VisibilityOutlinedIcon aria-hidden="true" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                error={Boolean(fieldErrors.password)}
                helperText={fieldErrors.password}
                onChange={() => clearFieldError("password")}
                required
              />
              {status && <Alert severity="error">{status}</Alert>}
              <Button className="primary-link" type="submit" disabled={isSubmitting}>
                Login
              </Button>
            </form>
          )}
          <div className="auth-switch">
            <span>{isRegister ? "Already have an account?" : "No account yet?"}</span>
            <Button component={Link} to={isRegister ? "/" : "/register"} type="button">
              {isRegister ? "Login" : "Create one"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
